import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { PersistConfig, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { ONBOARDING_STEPS, OnboardingStep } from './onboarding.utils';
import type { IMultipleVacationDocumentsOperation } from 'features/tasks/TaskOperations/taskOperations.types';

interface OnboardingState {
  steps: OnboardingStep[];
  activeStepIndex: number;
  isMinimized: boolean;
  employeeId?: string;
  taskId?: string;
  taskOperations?: IMultipleVacationDocumentsOperation;
}

const initialState: OnboardingState = {
  steps: ONBOARDING_STEPS,
  activeStepIndex: 0,
  isMinimized: false,
};

export const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    nextStep(state) {
      if (state.activeStepIndex < state.steps.length - 1) {
        state.activeStepIndex += 1;
      }
    },
    minimize(state) {
      state.isMinimized = true;
    },
    maximize(state) {
      state.isMinimized = false;
    },
    setEmployeeId: (state, action: PayloadAction<string>) => {
      state.employeeId = action.payload;
    },
    setTaskId: (state, action: PayloadAction<string>) => {
      state.taskId = action.payload;
    },
    setTaskOperations: (
      state,
      action: PayloadAction<IMultipleVacationDocumentsOperation>,
    ) => {
      state.taskOperations = action.payload;
    },
    clearOnboardingData: (state) => {
      delete state.employeeId;
      delete state.taskId;
      delete state.taskOperations;
    },
    finish: () => ({ ...initialState }),
  },
  selectors: {
    selectOnboardingActiveStepIndex: (state) => state.activeStepIndex,
    selectOnboardingStepEntity: (state) => state.steps[state.activeStepIndex],
    selectIsOnboardingMinimized: (state) => state.isMinimized,
    selectSteps: (state) => state.steps,
    selectEmployeeId: (state) => state.employeeId,
    selectOnboardingTaskId: (state) => state.taskId,
    selectTaskOperations: (state) => state.taskOperations,
  },
});

const persistConfig: PersistConfig<OnboardingState> = {
  key: 'onboarding',
  storage,
  version: CACHE_STORE_VERSION,
  blacklist: ['steps'],
};

export const onboardingPersistedReducer = persistReducer(
  persistConfig,
  onboardingSlice.reducer,
);
